<template>
  <div :id="`insurances-${listingId}`" class="space-y-16 p-16 md:p-24">
    <div v-if="shouldDisplayInsuranceOffers" class="space-y-16">
      <InsuranceSelection
        :insurance-offers
        :listing-id
        :model
        :selected-offer-id="selectedOffer?.id"
        @remove="handleRemove"
        @select="handleSelect"
      />

      <InsuranceCompliancy
        v-if="selectedOffer"
        :id="`compliancy-${listingId}`"
        :disabled="isPending"
        :offer="selectedOffer"
        @update="handleUpdateCompliancy"
      />
    </div>

    <template v-else>
      <SelectedCatchUpOffer
        v-if="selectedCatchupOffer"
        :disabled="isPending"
        :insurance-offer="selectedCatchupOffer"
        @remove="handleRemove"
        @update-compliancy="handleUpdateCompliancy"
      />
    </template>

    <p
      v-if="hasCompliancyError && hasBeenSubmitted"
      class="text-static-danger-hi mt-3"
    >
      {{ i18n(translations.compliancyError) }}
    </p>

    <CatchUpModal
      v-if="withCatchUpModal"
      :device-name="model"
      :insurance-offers
      @decline="handleDeclineCatchUpModal"
      @select-insurance-offer="handleSelectCatchUpModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type {
  InsuranceOffer,
  InsuranceOfferWithSelectedState,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useThrottleFn } from '@backmarket/utils/composables/useThrottleFn'
import { closeModal } from '@ds/components/ModalBase'

import { useGoNextStep } from '~/scopes/checkout/components/Cart/composables/useGoNextStep'
import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import InsuranceCompliancy from '~/scopes/insurance/components/InsuranceCompliancy/InsuranceCompliancy.vue'
import { DEFAULT_INSURANCE_ID } from '~/scopes/insurance/constants'
import { hasOfferCompliancyError } from '~/scopes/insurance/utils/insuranceOffers'

import translations from './Insurances.translations'
import CatchUpModal from './components/CatchUpModal/CatchUpModal.vue'
import InsuranceSelection from './components/InsuranceSelection/InsuranceSelection.vue'
import SelectedCatchUpOffer from './components/SelectedCatchUpOffer/SelectedCatchUpOffer.vue'

const props = defineProps<{
  withCatchUpModal: boolean
  listingId: string
  model: string
  insuranceOffers: InsuranceOfferWithSelectedState[]
}>()

const emit = defineEmits<{
  update: [updatedInsuranceOffers: InsuranceOfferWithSelectedState[]]
  next: []
}>()

const i18n = useI18n()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const { openSuccessToast, openErrorToast } = useTheToast()
const isPending = ref(false)
const $httpFetch = useHttpFetch()

const selectedOffer = ref<InsuranceOfferWithSelectedState | null>(
  props.insuranceOffers.find(
    (offer) => offer.selected && offer.id.toString() !== DEFAULT_INSURANCE_ID,
  ) || null,
)

const { hasBeenSubmitted } = useGoNextStep()

const hasCompliancyError = computed(() =>
  props.insuranceOffers.some(hasOfferCompliancyError),
)

const selectedCatchupOffer = computed(() => {
  return props.insuranceOffers.find((offer) => {
    return offer.isCatchUpOffer && offer.selected
  })
})

const shouldDisplayInsuranceOffers = computed(() => {
  const hasInsuranceOffers =
    props.insuranceOffers.filter(
      (offer) =>
        !offer.isCatchUpOffer && offer.id.toString() !== DEFAULT_INSURANCE_ID,
    ).length > 0

  if (!hasInsuranceOffers) return false

  // If catchup offer is selected, we want to display SelectedCacthupOffer instead
  if (selectedCatchupOffer.value) return false

  return true
})

const handleUpdateCompliancy = async (updatedInsurance: InsuranceOffer) => {
  if (selectedOffer.value) {
    selectedOffer.value = {
      ...updatedInsurance,
      selected: selectedOffer.value.selected,
    }
  }

  try {
    isPending.value = true

    await $httpFetch('/bm/cart/insurances/agreement', {
      method: 'POST',
      body: {
        listingId: props.listingId,
        insuranceOfferId: `${updatedInsurance.id}`,
        isAccepted: updatedInsurance.compliancy.isUserAgreementAccepted,
      },
    })

    const updatedInsuranceOffers = props.insuranceOffers.reduce<
      InsuranceOfferWithSelectedState[]
    >((offers, offer) => {
      if (offer.id === selectedOffer.value?.id) {
        return [...offers, selectedOffer.value]
      }

      return [...offers, offer]
    }, [])

    emit('update', updatedInsuranceOffers)
  } catch (error) {
    await handleUnauthorizedUser(
      error as Record<string, unknown>,
      '[CHECKOUT] Unhandled error updating terms and conditions',
    )

    throw error
  } finally {
    isPending.value = false
  }
}

const updateSelectedInsuranceInCart = useThrottleFn(
  async function updateSelectedInsuranceInCart() {
    try {
      await $httpFetch('/bm/cart/update_insurance_offer', {
        method: 'POST',
        body: {
          listing_id: props.listingId,
          insurance_offer_id: `${selectedOffer.value?.id ?? ''}`,
        },
      })

      const updatedInsuranceOffers = props.insuranceOffers.reduce<
        InsuranceOfferWithSelectedState[]
      >((offers, offer) => {
        if (offer.id === selectedOffer.value?.id) {
          return [...offers, selectedOffer.value]
        }

        return [...offers, { ...offer, selected: false }]
      }, [])

      emit('update', updatedInsuranceOffers)
    } catch (error) {
      await handleUnauthorizedUser(
        error as Record<string, unknown>,
        '[CHECKOUT] Unhandled error updating insurance offer',
      )

      throw error
    }
  },
  1000,
  true,
)

const handleSelect = async (updatedInsurance: InsuranceOffer) => {
  selectedOffer.value = { ...updatedInsurance, selected: true }
  await updateSelectedInsuranceInCart()
}

const handleRemove = async () => {
  selectedOffer.value = null
  await updateSelectedInsuranceInCart()
}

async function handleSelectCatchUpModal(insuranceOffer: InsuranceOffer) {
  closeModal()
  sessionStorage.setItem('showCatchupModal', 'false')

  try {
    selectedOffer.value = { ...insuranceOffer, selected: true }
    await updateSelectedInsuranceInCart()

    openSuccessToast({
      content: i18n(translations.successfullyAddedToast),
    })
  } catch (error) {
    openErrorToast()

    throw error
  }
}

const handleDeclineCatchUpModal = () => {
  closeModal()
  sessionStorage.setItem('showCatchupModal', 'false')
  emit('next')
}
</script>
